import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import {
  BookOpen, LayoutDashboard, LogOut, GraduationCap, Settings
} from 'lucide-react';

function NavItem({ to, icon: Icon, label, active }) {
  return (
    <Link
      to={to}
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150',
        active
          ? 'bg-accent-soft text-accent-ink'
          : 'text-muted hover:bg-sunken hover:text-ink'
      )}
    >
      <Icon className={cn('w-4 h-4 flex-shrink-0', active ? 'text-accent' : 'text-faint')} />
      {label}
    </Link>
  );
}

const roleLabels = { student: 'Estudiante', teacher: 'Profesor', simulacion: 'Simulación' };

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleLogout = () => { logout(); navigate('/login'); };

  const studentNav = [
    { to: '/student/quiz', icon: BookOpen, label: 'Quiz' },
    { to: '/student/dashboard', icon: LayoutDashboard, label: 'Mi progreso' },
  ];
  const teacherNav = [
    { to: '/teacher/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  ];
  const navItems = user?.role === 'teacher' ? teacherNav : studentNav;

  return (
    <div className="flex h-screen bg-canvas overflow-hidden">
      <aside className="w-56 flex-shrink-0 flex flex-col border-r border-line bg-surface">
        {/* Logo */}
        <div className="px-5 h-14 flex items-center border-b border-line">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-accent rounded-lg flex items-center justify-center flex-shrink-0">
              <GraduationCap className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-ink text-sm tracking-tight">Quiz App</span>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
          <p className="px-3 pt-1 pb-2 text-[11px] font-semibold uppercase tracking-wider text-faint">
            {user?.role === 'teacher' ? 'Docencia' : 'Estudio'}
          </p>
          {navItems.map(item => (
            <NavItem key={item.to} {...item} active={isActive(item.to)} />
          ))}
        </nav>

        {/* Bottom actions */}
        <div className="px-3 py-3 border-t border-line space-y-0.5">
          <NavItem to="/settings" icon={Settings} label="Configuración" active={isActive('/settings')} />

          <div className="flex items-center gap-3 px-3 py-2 mt-1">
            <div className="w-7 h-7 rounded-full bg-accent-soft flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-semibold text-accent-ink">
                {user?.name?.charAt(0)?.toUpperCase() || user?.student_number?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-ink truncate">{user?.name || user?.student_number}</p>
              <p className="text-xs text-faint truncate">{roleLabels[user?.role] || user?.role}</p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-muted hover:bg-danger-soft hover:text-danger-ink transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden">
        {children}
      </main>
    </div>
  );
}
